<script lang="ts">
export default {
  name: 'BinderReviewNav',
};
</script>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { ChevronLeft, ChevronRight } from '@lucide/vue';
import AppButton from '@/components/v2/AppButton.vue';
import ConfirmModal from '@/components/v2/ConfirmModal.vue';

// Steps through the client's extraction results without going back to the policies list.
// The sequence spans both binder sources, so Next can land on an external submission from
// an internal binder and the other way round.
const props = withDefaults(
  defineProps<{
    hasPrev: boolean;
    hasNext: boolean;
    position?: string;
    // Edits sitting unsaved in the field list. Leaving would drop them, so the reviewer
    // is asked first rather than losing them on a stray click.
    unsavedCount?: number;
  }>(),
  { position: '', unsavedCount: 0 },
);

const emit = defineEmits<{
  prev: [];
  next: [];
}>();

const confirmModal = ref<InstanceType<typeof ConfirmModal> | null>(null);

const hasUnsavedEdits = computed(() => props.unsavedCount > 0);

async function go(direction: 'prev' | 'next') {
  if (hasUnsavedEdits.value) {
    const plural = props.unsavedCount === 1 ? '' : 's';
    const confirmed = await confirmModal.value?.showConfirm(
      `You have ${props.unsavedCount} unsaved change${plural} on this result. Leaving now discards ${props.unsavedCount === 1 ? 'it' : 'them'}.`,
    );
    if (!confirmed) {
      return;
    }
  }
  if (direction === 'prev') {
    emit('prev');
  } else {
    emit('next');
  }
}
</script>

<template>
  <div
    class="flex items-center gap-2"
    data-testid="binder-review-nav">
    <AppButton
      outline
      :disabled="!hasPrev"
      data-testid="binder-review-prev"
      @click="go('prev')">
      <ChevronLeft :size="16" /> Back
    </AppButton>
    <span
      v-if="position"
      class="text-xs whitespace-nowrap text-base-content/60"
      data-testid="binder-review-position">
      {{ position }}
    </span>
    <AppButton
      outline
      :disabled="!hasNext"
      data-testid="binder-review-next"
      @click="go('next')">
      Next <ChevronRight :size="16" />
    </AppButton>

    <ConfirmModal ref="confirmModal" />
  </div>
</template>
